<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplateParser from './WhatsappTemplateParser.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';

const props = defineProps({
  messageTemplates: {
    type: Array,
    default: () => [],
  },
  targetInbox: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();

// TODO: Remove this when we support all formats
const formatsToRemove = ['DOCUMENT', 'IMAGE', 'VIDEO'];

const searchQuery = ref('');
const selectedTemplate = ref(null);

const showTemplatesMenu = ref(false);

const whatsAppTemplateMessages = computed(() => {
  // Add null check and ensure it's an array
  const templates = Array.isArray(props.messageTemplates)
    ? props.messageTemplates
    : [];

  // TODO: Remove the last filter when we support all formats
  return templates
    .filter(template => template?.status?.toLowerCase() === 'approved')
    .filter(template => {
      return template?.components?.every(component => {
        return !formatsToRemove.includes(component.format);
      });
    });
});

const filteredTemplates = computed(() => {
  return whatsAppTemplateMessages.value.filter(template =>
    template.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const getTemplateBody = template => {
  return template.components.find(component => component.type === 'BODY').text;
};

const handleTriggerClick = () => {
  searchQuery.value = '';
  showTemplatesMenu.value = !showTemplatesMenu.value;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
  showTemplatesMenu.value = false;
};

const handleBack = () => {
  selectedTemplate.value = null;
  showTemplatesMenu.value = true;
};

const handleSendMessage = template => {
  emit('sendMessage', template);
  selectedTemplate.value = null;
};

const isAWhatsAppCloudChannel = computed(() => {
  const provider = props.targetInbox?.provider || '';
  const channelType = props.targetInbox?.channelType || '';
  return channelType === INBOX_TYPES.WHATSAPP && provider === 'whatsapp_cloud';
});
</script>

<template>
  <div class="relative">
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
      @click="handleTriggerClick"
    />
    <div
      v-if="showTemplatesMenu"
      class="absolute top-full mt-1.5 max-h-96 overflow-y-auto left-0 flex flex-col gap-2 p-4 items-center w-[650px] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg template__list-container"
    >
      <div class="relative w-full templates__list-search">
        <span class="absolute i-lucide-search size-3.5 top-2 left-3" />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="
            t(
              'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
            )
          "
          class="w-full h-8 py-2 pl-10 pr-2 text-sm border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 templates__search-input"
        />
      </div>
      <div
        v-for="(template, i) in filteredTemplates"
        :key="template.id"
        class="flex flex-col w-full "
        @click="handleTemplateClick(template)"
      >
      <div class="template__list-item">
          <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </span>
          <span
            class="px-2 py-0.5 text-xs rounded
                  bg-n-alpha-2 text-n-slate-11"
          >
            {{ template.language }}
          </span>
        </div>

        <div>
          <p class="text-xs font-semibold text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.LABELS.TEMPLATE_BODY') }}
          </p>
          <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-3 font-mono">
            {{ getTemplateBody(template) }}
          </p>
        </div>

        <div class="text-xs text-n-slate-11">
          <span class="font-semibold">
            {{ t('WHATSAPP_TEMPLATES.PICKER.LABELS.CATEGORY') }}:
          </span>
          {{ template.category }}
        </div>

        <div class="text-xs text-n-slate-11">
          <span class="font-semibold">
            {{ t('WHATSAPP_TEMPLATES.PICKER.LABELS.HEADER') }}:
          </span>
          {{ template.templateType }}
        </div>
      </div>
     
      <div
        v-if="i !== filteredTemplates.length - 1"
        class="h-px w-[95%] mx-auto bg-slate-300 dark:bg-slate-700 my-2"
      />
      </div>
      <template v-if="filteredTemplates.length === 0">
        <p class="w-full pt-2 text-sm text-n-slate-11">
          {{ t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE') }}
        </p>
      </template>
    </div>
    <WhatsappTemplateParser
      v-if="selectedTemplate && !isAWhatsAppCloudChannel"
      :template="selectedTemplate"
      @send-message="handleSendMessage"
      @back="handleBack"
    />
    <WhatsappTemplate
      v-else-if="selectedTemplate && isAWhatsAppCloudChannel"
      :template="selectedTemplate"
      @send-message="handleSendMessage"
      @back="handleBack"
    />
  </div>
</template>


<style>

.templates__list-search {
  @apply items-center flex bg-slate-25 dark:bg-slate-900 mb-2.5 py-0 px-2.5 rounded-md border border-solid border-slate-100 dark:border-slate-700;

  .search-icon {
    @apply text-slate-400 dark:text-slate-300;
  }

  .templates__search-input {
    @apply bg-transparent border-0 text-xs h-9 m-0;
  }
}
.template__list-container {
  @apply bg-slate-25 dark:bg-slate-900 rounded-md max-h-[18.75rem] overflow-y-auto p-2.5;

  .template__list-item {
    @apply rounded-lg cursor-pointer block p-2.5 text-left w-full hover:bg-woot-50 dark:hover:bg-slate-800;

    .label-title {
      @apply text-sm;
    }

    .label-category {
      @apply mt-5;

      span {
        @apply text-sm font-semibold;
      }
    }

    .label-body {
      font-family: monospace;
    }
  }
}
.strong {
  @apply text-xs font-semibold;
}

hr {
  @apply border-b border-solid border-slate-100 dark:border-slate-700 my-2.5 mx-auto max-w-[95%];
}
</style>